/*
 * newsbot - Actions section for filter details
 */

import { useRef } from "react";
import { Field, FieldArray, useFormikContext } from "formik";
import type { FieldProps, FieldArrayRenderProps } from "formik";
import { ChevronRightIcon, BoltIcon } from "@heroicons/react/24/solid";

import { classNames } from "@utils";
import { useToggle } from "@hooks/hooks";
import { useDeleteFilterAction } from "@api/queries";

import { Select, TextField, NumberField } from "@components/inputs";
import { DeleteModal } from "@components/modals";
import { EmptyListState } from "@components/emptystates";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";

import { Checkbox } from "@/components/Checkbox";

import { FilterHalfRow, FilterLayout, FilterPage, FilterSection, CollapsibleSection } from "@/components/filters/_components";
import { Webhook, Tag, Queue } from "./action_components";

import type { FilterAction } from "@/types";

const ActionTypeOptions = [
  { label: "Webhook (n8n)", value: "webhook_n8n" },
  { label: "Webhook (Cuppa)", value: "webhook_cuppa" },
  { label: "Add Tags", value: "tag" },
  { label: "Add to Queue", value: "queue" },
];

const ActionTypeNameMap: Record<string, string> = {
  webhook_n8n: "n8n Webhook",
  webhook_cuppa: "Cuppa Webhook",
  tag: "Add Tags",
  queue: "Queue",
};

interface ActionsFormValues {
  id: string;
  actions: FilterAction[];
}

export function Actions() {
  const { values } = useFormikContext<ActionsFormValues>();

  const newAction: Partial<FilterAction> = {
    id: "",
    filter_id: values.id,
    name: "new action",
    enabled: true,
    type: "webhook_n8n",
    priority: 0,
    webhook_endpoint: "",
    webhook_method: "POST",
    webhook_headers: {},
    webhook_payload_template: "",
    webhook_expected_status: 200,
    webhook_retry_attempts: 3,
    webhook_retry_delay_seconds: 5,
    tags_to_add: [],
    queue_name: null,
  };

  return (
    <CollapsibleSection
      defaultOpen={values.actions && values.actions.length > 0}
      title="Actions"
      subtitle="Execute webhooks, add tags, or queue items when filter matches"
    >
      <FieldArray name="actions">
        {({ remove, push }: FieldArrayRenderProps) => (
          <div className="col-span-12">
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                className="relative inline-flex items-center px-4 py-2 border border-transparent transition shadow-xs text-sm font-medium rounded-md text-white bg-blue-600 dark:bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-500"
                onClick={() => push(newAction)}
              >
                <BoltIcon
                  className="w-5 h-5 mr-1"
                  aria-hidden="true"
                />
                Add Action
              </button>
            </div>

            {values.actions && values.actions.length > 0 ? (
              <ul className="rounded-md">
                {values.actions.map((action: FilterAction, index: number) => (
                  <FilterActionsItem
                    key={action.id || index}
                    action={action}
                    idx={index}
                    initialEdit={values.actions.length === 1}
                    remove={remove}
                    filterId={values.id}
                  />
                ))}
              </ul>
            ) : (
              <EmptyListState text="No actions yet!" />
            )}
          </div>
        )}
      </FieldArray>
    </CollapsibleSection>
  );
}

const TypeForm = ({ action, idx }: { action: FilterAction; idx: number }) => {
  switch (action.type) {
    case "webhook_n8n":
    case "webhook_cuppa":
      return <Webhook idx={idx} action={action} />;
    case "tag":
      return <Tag idx={idx} />;
    case "queue":
      return <Queue idx={idx} />;
    default:
      return null;
  }
};

interface FilterActionsItemProps {
  action: FilterAction;
  idx: number;
  initialEdit: boolean;
  remove: <T>(index: number) => T | undefined;
  filterId: string;
}

function FilterActionsItem({ action, idx, initialEdit, remove, filterId }: FilterActionsItemProps) {
  const cancelButtonRef = useRef(null);

  const [deleteModalIsOpen, toggleDeleteModal] = useToggle(false);
  const [edit, toggleEdit] = useToggle(initialEdit);

  const deleteMutation = useDeleteFilterAction(filterId);

  const removeAction = () => {
    if (action.id) {
      deleteMutation.mutate(action.id, {
        onSuccess: () => {
          remove(idx);
          toast.custom((t) => (
            <Toast type="success" body={`Action ${action?.name} was deleted`} t={t} />
          ));
        },
        onError: (error) => {
          toast.custom((t) => (
            <Toast type="error" body={`Failed to delete action: ${String(error)}`} t={t} />
          ));
        },
      });
    } else {
      remove(idx);
    }
  };

  return (
    <li>
      <div
        className={classNames(
          idx % 2 === 0
            ? "bg-white dark:bg-gray-775"
            : "bg-gray-100 dark:bg-gray-815",
          "flex items-center transition px-2 sm:px-6 rounded-md my-1 border border-gray-150 dark:border-gray-750 hover:bg-gray-200 dark:hover:bg-gray-850"
        )}
      >
        <Field name={`actions.${idx}.enabled`} type="checkbox">
          {({
            field,
            form: { setFieldValue }
          }: FieldProps) => (
            <Checkbox
              {...field}
              value={!!field.checked}
              setValue={(value: boolean) => {
                setFieldValue(field.name, value);
              }}
            />
          )}
        </Field>

        <button className="pl-2 pr-0 sm:px-4 py-4 w-full flex items-center" type="button" onClick={toggleEdit}>
          <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex text-sm truncate">
              <p className="font-medium text-dark-600 dark:text-gray-100 truncate">
                {action.name}
              </p>
            </div>
            <div className="shrink-0 sm:mt-0 sm:ml-5">
              <div className="flex overflow-hidden -space-x-1">
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  {ActionTypeNameMap[action.type]}
                </span>
              </div>
            </div>
          </div>
          <div className="ml-5 shrink-0">
            <ChevronRightIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
        </button>

      </div>
      {edit && (
        <div className="flex items-center mt-1 px-3 sm:px-5 rounded-md border border-gray-150 dark:border-gray-750">
          <DeleteModal
            isOpen={deleteModalIsOpen}
            isLoading={deleteMutation.isPending}
            buttonRef={cancelButtonRef}
            toggle={toggleDeleteModal}
            deleteAction={removeAction}
            title="Remove filter action"
            text="Are you sure you want to remove this action? This action cannot be undone."
          />

          <FilterPage gap="sm:gap-y-6">
            <FilterSection
              title="Action"
              subtitle="Define the action type and its name"
            >
              <FilterLayout>
                <FilterHalfRow>
                  <Select
                    name={`actions.${idx}.type`}
                    label="Action type"
                    optionDefaultText="Select type"
                    options={ActionTypeOptions}
                    tooltip={<div><p>Select the action type for this action.</p></div>}
                  />
                </FilterHalfRow>

                <FilterHalfRow>
                  <TextField name={`actions.${idx}.name`} label="Name" />
                </FilterHalfRow>

                <div className="col-span-12 sm:col-span-6">
                  <NumberField
                    name={`actions.${idx}.priority`}
                    label="Priority"
                    placeholder="0"
                    tooltip={<p>Higher priority actions execute first</p>}
                  />
                </div>
              </FilterLayout>
            </FilterSection>

            <TypeForm action={action} idx={idx} />

            <div className="pt-6 pb-4 flex space-x-2 justify-between">
              <button
                type="button"
                className="inline-flex items-center justify-center px-4 py-2 rounded-md sm:text-sm bg-red-700 dark:bg-red-900 dark:hover:bg-red-700 hover:bg-red-800 text-white focus:outline-hidden"
                onClick={toggleDeleteModal}
              >
                Remove Action
              </button>

              <button
                type="button"
                className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-xs text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-hidden"
                onClick={toggleEdit}
              >
                Close
              </button>
            </div>
          </FilterPage>
        </div>
      )}
    </li>
  );
}
